import JSONBigInt from "@nsftx/json-bigint";

import type { GridPaginationInfo } from "@/v2/framework/view/types";

const JSONBigIntNative = JSONBigInt({ storeAsString: true });

/** Parsed JSON body from the API — shape varies by endpoint. */
export type ApiResponse = Record<string, unknown>;

export function parseHttpBody(raw: string): unknown {
  if (!raw) return null;
  return JSONBigIntNative.parse(raw);
}

export function getApiErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (typeof o.message === "string" && o.message.trim()) return o.message.trim();
    if (typeof o.error === "string" && o.error.trim()) return o.error.trim();
  }
  return fallback;
}

export function parseRecordList(data: unknown): Record<string, unknown>[] {
  if (!Array.isArray(data)) return [];
  return data.map((row) => ({ ...(row as Record<string, unknown>) }));
}

/** Primary payload from a backend JSON body — `data` field when present, otherwise the body itself. */
export function readApiData(body: unknown): unknown {
  if (body == null || typeof body !== "object") return body;
  const record = body as ApiResponse;
  return record.data !== undefined ? record.data : body;
}

/** Reads `paginationInfo` or `pagination` from a grid list response (V1-compatible). */
export function parseGridPaginationInfo(body: unknown): GridPaginationInfo | undefined {
  if (body == null || typeof body !== "object") return undefined;

  const record = body as ApiResponse;
  const raw = record.paginationInfo ?? record.pagination;
  if (!raw || typeof raw !== "object") return undefined;

  const pagination = raw as Record<string, unknown>;
  const total = Number(pagination.total) || 0;
  const size = Number(pagination.size) || 20;
  const page = Number(pagination.page) || 1;
  const pages =
    Number(pagination.pages) || (total > 0 && size > 0 ? Math.ceil(total / size) : 0);

  return { page, pages, size, total };
}
